import _ from 'lodash';

import {
  addParamsToUrl,
  deduplicateSlashes,
  ensureTrailingSlash,
  stripTrailingSlash,
} from '@zapier/url-utils';
import {
  deleteJson,
  getJson,
  patchJson,
  postJson,
} from '@zapier/toolbox-browser-fetch';

const defaultFormatUrl = (baseUrl, path) => `${baseUrl}/${path}`.trim();

const stripSlashes = _.flow(deduplicateSlashes, stripTrailingSlash);

const stripSlashesExceptLast = _.flow(deduplicateSlashes, ensureTrailingSlash);

const buildEntityApi = (
  entityType,
  baseUrl,
  customize = _.identity,
  options = {}
) => {
  const formatUrl = _.isFunction(baseUrl)
    ? baseUrl
    : _.partial(defaultFormatUrl, baseUrl);
  const handleSlashes = options.ensureTrailingSlash
    ? stripSlashesExceptLast
    : stripSlashes;
  const api = {
    collectionEndpoint: (path = '', getState) =>
      handleSlashes(formatUrl(path, getState)),
    modelEndpoint: (id, getState) =>
      handleSlashes(api.collectionEndpoint(id, getState)),
  };

  // Highly customizable API functions
  api.createWith = (transformUrl, data, getState) =>
    postJson(transformUrl(api.collectionEndpoint(undefined, getState)), data);

  api.deleteWith = (transformUrl, id, data, getState) =>
    deleteJson(transformUrl(api.modelEndpoint(id, getState)), data);

  api.loadWith = (transformUrl, id, getState) =>
    getJson(transformUrl(api.modelEndpoint(id, getState)));

  api.saveWith = (transformUrl, id, delta, getState) =>
    patchJson(transformUrl(api.modelEndpoint(id, getState)), delta);

  api.loadCollectionWith = (transformUrl, getState) => {
    return getJson(
      transformUrl(api.collectionEndpoint(undefined, getState))
    ).then(data => {
      return data;
    });
  };

  api.saveCollectionWith = (transformUrl, delta, getState) =>
    patchJson(transformUrl(api.collectionEndpoint(undefined, getState)), delta);

  // Default API functions
  api.create = _.partial(api.createWith, _.identity);
  api.delete = _.partial(api.deleteWith, _.identity);
  api.load = _.partial(api.loadWith, _.identity);
  api.save = _.partial(api.saveWith, _.identity);
  api.loadCollection = _.partial(api.loadCollectionWith, _.identity);
  api.saveCollection = _.partial(api.saveCollectionWith, _.identity);

  // API functions that support query params.
  const withParams = apiMethod => (params, ...args) =>
    apiMethod(url => addParamsToUrl(url, params), ...args);
  api.createWithParams = withParams(api.createWith);
  api.loadWithParams = withParams(api.loadWith);
  api.saveWithParams = withParams(api.saveWith);
  api.loadCollectionWithParams = withParams(api.loadCollectionWith);

  customize(api);
  return api;
};

export default buildEntityApi;
